import { useState, useEffect } from "react";

// Teams-style palette: dark enough that white text stays readable on every entry.
const COLORS = [
  "#6264A7", // Teams purple
  "#0078D4", // blue
  "#038387", // teal
  "#107C10", // green
  "#498205", // olive
  "#8764B8", // light purple
  "#C239B3", // magenta
  "#E3008C", // pink
  "#CA5010", // orange
  "#A4262C", // red
  "#005B70", // dark teal
  "#986F0B", // dark yellow
  "#4F6BED", // royal blue
  "#847545", // brass
  "#7A7574", // gray
];

// Same name always lands on the same color (djb2-ish string hash).
const colorFor = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return COLORS[Math.abs(hash) % COLORS.length];
};

// "John Smith" -> "JS", "john" -> "J", "john.smith@x.com" -> "JS"
export const getInitials = (name, email) => {
  let source = (name || "").trim();

  if (!source && email) {
    // Fall back to the local part of the email, split on separators.
    source = email.split("@")[0].replace(/[._-]+/g, " ").trim();
  }
  if (!source) return "?";

  const words = source.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Avatar that shows the user's photo when there is one, and otherwise falls back
 * to Teams-style initials on a color derived from the name.
 */
const InitialsAvatar = ({
  name,
  email,
  src,
  size = 40,
  className = "",
  onClick,
  title,
}) => {
  const [failed, setFailed] = useState(false);

  // A new/refreshed URL deserves another attempt (presigned URLs expire).
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showImage = Boolean(src) && !failed;

  if (showImage) {
    return (
      <img
        src={src}
        alt={name || email || "User avatar"}
        title={title}
        onClick={onClick}
        onError={() => setFailed(true)}
        style={{ width: size, height: size }}
        className={`rounded-full border border-gray-300 object-cover ${className}`}
      />
    );
  }

  const initials = getInitials(name, email);
  const background = colorFor((name || email || "?").toLowerCase());

  return (
    <div
      title={title}
      onClick={onClick}
      aria-label={name || email || "User avatar"}
      style={{
        width: size,
        height: size,
        backgroundColor: background,
        fontSize: Math.round(size * 0.4),
      }}
      className={`rounded-full flex items-center justify-center font-semibold text-white select-none leading-none ${className}`}
    >
      {initials}
    </div>
  );
};

export default InitialsAvatar;
